import React, { useState, useImperativeHandle, forwardRef, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { IFoldingView, INode, GraphComponent } from '@yfiles/yfiles';
import './ProgressiveCollapse.css';
import CollapseLevelMenu from './CollapseLevelMenu';

/**
 * ProgressiveCollapse component with URL state sharing
 * 
 * State is synchronized with URL search parameters:
 * - `collapseLevel`: number (0-4) - controls the collapse level
 * 
 * Example URL: ?collapseLevel=2
 * 
 * The state can be shared by copying the URL, and the component will restore
 * the collapse state when loaded with those parameters.
 */

interface ProgressiveCollapseProps {
  foldingView: IFoldingView | null;
  graphComponentInstance: GraphComponent | null;
  runLayoutAndUpdate: (gc: GraphComponent, morph?: boolean) => Promise<void>;
  parentInitialized?: boolean;
}

export interface ProgressiveCollapseHandle {
  handleExpandAll: () => void;
  handleCollapseAll: () => void;
}

const ProgressiveCollapse = forwardRef<ProgressiveCollapseHandle, ProgressiveCollapseProps>(
  ({ foldingView, graphComponentInstance, runLayoutAndUpdate, parentInitialized = true }, ref) => {
    const [searchParams, setSearchParams] = useSearchParams();
    
  const getInitialCollapseLevel = () => {
    const param = searchParams.get('dataLevel');
    const level = param ? parseInt(param, 10) : 1;
    const result = isNaN(level) || level < 0 || level > 4 ? 1 : level;
    return result;
  };
    
    const [collapseLevel, setCollapseLevel] = useState<number>(getInitialCollapseLevel);
    const [isInitialized, setIsInitialized] = useState<boolean>(false);

  const levelDescriptions = ['Operating Model', 'Organisation', 'Technology', 'Process & Data'];

  const handleExpandAll = useCallback(async (skipLayout = false) => {
    if (foldingView && graphComponentInstance) {
      // Expand capability nodes
      const capabilityNodesToExpand: INode[] = [];
      foldingView.graph.nodes.forEach((node: INode) => {
        if (node.tag?.type === 'CAPABILITY' && !foldingView.isExpanded(node)) {
          capabilityNodesToExpand.push(node);
        }
      });

      for (const node of capabilityNodesToExpand) {
        foldingView.expand(node);
      }

      // Expand team nodes
      const teamNodesToExpand: INode[] = [];
      foldingView.graph.nodes.forEach((node: INode) => {
        if (node.tag?.type === 'TEAM' && !foldingView.isExpanded(node)) {
          teamNodesToExpand.push(node);
        }
      });

      for (const node of teamNodesToExpand) {
        foldingView.expand(node);
      }

      // Expand application group nodes
      const appGroupNodesToExpand: INode[] = [];
      foldingView.graph.nodes.forEach((node: INode) => {
        if (node.tag?.type === 'APPLICATION_GROUP' && !foldingView.isExpanded(node)) {
          appGroupNodesToExpand.push(node);
        }
      });

      for (const node of appGroupNodesToExpand) {
        foldingView.expand(node);
      }

      // Run layout after all expansions, if not skipped
      if (!skipLayout) {
        await runLayoutAndUpdate(graphComponentInstance, true);
      }
    }
  }, [foldingView, graphComponentInstance, runLayoutAndUpdate]);

    const handleCollapseAll = useCallback(async () => {
      if (foldingView && graphComponentInstance) {
        // Collapse application group nodes
        const appGroupNodesToCollapse: INode[] = [];
        foldingView.graph.nodes.forEach((node: INode) => {
          if (node.tag?.type === 'APPLICATION_GROUP' && foldingView.isExpanded(node)) {
            appGroupNodesToCollapse.push(node);
          }
        });

        for (const node of appGroupNodesToCollapse) {
          foldingView.collapse(node);
        }

        // Collapse team nodes
        const teamNodesToCollapse: INode[] = [];
        foldingView.graph.nodes.forEach((node: INode) => {
          if (node.tag?.type === 'TEAM' && foldingView.isExpanded(node)) {
            teamNodesToCollapse.push(node);
          }
        });

        for (const node of teamNodesToCollapse) {
          foldingView.collapse(node);
        }

        // Collapse capability nodes
        const capabilityNodesToCollapse: INode[] = [];
        foldingView.graph.nodes.forEach((node: INode) => {
          if (node.tag?.type === 'CAPABILITY' && foldingView.isExpanded(node)) {
            capabilityNodesToCollapse.push(node);
          }
        });

        for (const node of capabilityNodesToCollapse) {
          foldingView.collapse(node);
        }

        await runLayoutAndUpdate(graphComponentInstance, false);
      }
    }, [foldingView, graphComponentInstance, runLayoutAndUpdate]);

    const handleCollapseLevel = useCallback(
      async (level: number) => {
        if (!foldingView || !graphComponentInstance) {
          return;
        }

        await handleExpandAll(true);

        const graph = foldingView.graph;

        if (level <= 3) {
          const appGroupNodes = graph.nodes
            .toArray()
            .filter(
              (node: INode) =>
                graph.isGroupNode(node) &&
                foldingView.isExpanded(node) &&
                node.tag?.type === 'APPLICATION_GROUP'
            );
          appGroupNodes.forEach((node: INode) => foldingView.collapse(node));
        }

        if (level <= 2) {
          const teamNodes = graph.nodes
            .toArray()
            .filter(
              (node: INode) =>
                graph.isGroupNode(node) && foldingView.isExpanded(node) && node.tag?.type === 'TEAM'
            );
          teamNodes.forEach((node: INode) => foldingView.collapse(node));
        }

        if (level <= 1) {
          const capabilityNodes = graph.nodes
            .toArray()
            .filter(
              (node: INode) =>
                graph.isGroupNode(node) &&
                foldingView.isExpanded(node) &&
                node.tag?.type === 'CAPABILITY'
            );
          capabilityNodes.forEach((node: INode) => foldingView.collapse(node));
        }

        setCollapseLevel(level);
        await runLayoutAndUpdate(graphComponentInstance, false);
      },
      [foldingView, graphComponentInstance, handleExpandAll, runLayoutAndUpdate]
    );

    useImperativeHandle(ref, () => ({
      handleExpandAll,
      handleCollapseAll
    }));

  // Sync state to URL when it changes (only after initialization)
  useEffect(() => {
    if (!isInitialized) return;
    
    // Read current URL params at update time to avoid race conditions
    const currentParams = new URLSearchParams(window.location.search);
    currentParams.set('dataLevel', collapseLevel.toString());
    setSearchParams(currentParams, { replace: true });
  }, [collapseLevel, isInitialized, setSearchParams]);

  // Apply collapse level from URL on mount (wait for parent to initialize first)
  useEffect(() => {
    if (!foldingView || !graphComponentInstance || isInitialized || !parentInitialized) {
      return;
    }
    
    const urlLevel = getInitialCollapseLevel();
    if (urlLevel !== 1) {
      // Apply the collapse level from URL
      handleCollapseLevel(urlLevel).then(() => {
        setIsInitialized(true);
      });
    } else {
      setIsInitialized(true);
    }
  }, [foldingView, graphComponentInstance, parentInitialized]);

  return (
    <CollapseLevelMenu
      visible={true}
      selectedValue={collapseLevel}
      onChange={handleCollapseLevel}
      items={levelDescriptions}
      title="Architecture"
    />
  );
});

export default ProgressiveCollapse;
